import Phaser from "phaser";
import { getSafeAreaInsetLeft, getSafeAreaInsetRight } from "../../../utils/mobile-utils";
import { CraftWidget, RecipeWidget, MerchantWidget, DailyChallengeWidget, GauntletWidget, drawBlackPanelBackground, QUEST_WIDGET_W } from "./quest-widgets";
import { DailyStarsComponent } from "../../../ui/daily-stars-component";
import { ModernButton } from "../../../ui/modern-button";
import { Recipe, ItemDefinition, Item, MerchantInfo, StationInfo } from "../../../models/common";
import { DailyChallengeClientData } from "../../../models/daily-challenge";
import { fetchItemDefs, resetItemDefsCache } from "../../../item-catalog";
import { showConfetti } from "../../../ui/splash-particle";

let recipesPromise: Promise<Recipe[]> | null = null;
let merchantsPromise: Promise<MerchantInfo[]> | null = null;
let stationsPromise: Promise<StationInfo[]> | null = null;

function fetchRecipes(): Promise<Recipe[]> {
  if (!recipesPromise) recipesPromise = fetch("/data/recipes.json").then((r) => r.json());
  return recipesPromise;
}

function fetchMerchants(): Promise<MerchantInfo[]> {
  if (!merchantsPromise) merchantsPromise = fetch("/data/merchants.json").then((r) => r.json());
  return merchantsPromise;
}

function fetchStations(): Promise<StationInfo[]> {
  if (!stationsPromise) stationsPromise = fetch("/data/stations.json").then((r) => r.json());
  return stationsPromise;
}

export class QuestsScreen {
  private static readonly DISCORD_INVITE_URL = "https://discord.com/invite/WdkYnRj9Hk";

  private readonly panel: Phaser.GameObjects.Container;

  private readonly scene: Phaser.Scene;

  private readonly onOpenGauntlet: () => void;

  // Bumped on every show() so a late-arriving daily-challenge fetch from a previous
  // show() call can tell it's stale and skip adding its widget to a rebuilt panel.
  private showToken: number = 0;
  private dailyChallengeListener: ((data: DailyChallengeClientData | null) => void) | null = null;
  private dailyChallengeTimeout: ReturnType<typeof setTimeout> | null = null;
  // Set by celebrateDailyChallengeCompletion() before navigating here from match-end —
  // consumed by the daily-challenge widget once it actually renders, since it loads
  // asynchronously and may not exist yet at the moment show() runs.
  private pendingDailyChallengeCelebration: boolean = false;
  // Last server response, rendered immediately on show() so the widget doesn't blink
  // out while a fresh fetch round-trips — show() re-runs on every carousel drag.
  private lastDailyChallengeData: DailyChallengeClientData | null = null;

  // All widgets are added here (not directly to `panel`).
  private contentContainer: Phaser.GameObjects.Container | null = null;

  constructor(
    scene: Phaser.Scene,
    panel: Phaser.GameObjects.Container,
    onOpenGauntlet: () => void,
  ) {
    this.scene = scene;
    this.panel = panel;
    this.onOpenGauntlet = onOpenGauntlet;
  }

  // Call before navigating here (e.g. from the match-end queue) to celebrate a just-
  // completed daily challenge day — confetti plus a one-off bounce on its widget once
  // it renders.
  celebrateDailyChallengeCompletion(): void {
    this.pendingDailyChallengeCelebration = true;
  }

  async show(): Promise<void> {
    const token = ++this.showToken;
    this.stopListeningForDailyChallenge();
    // Captured now (not read live inside the async fetch callback below) so a stale
    // flag can't leak into a later show() call, and a timed-out fetch can't leave it
    // stuck set forever.
    const celebrateOnRender = this.pendingDailyChallengeCelebration;
    this.pendingDailyChallengeCelebration = false;

    // Remove previous children (if any)
    this.panel.removeAll(true);

    this.contentContainer = this.scene.add.container(0, 0);
    this.panel.add(this.contentContainer);
    const content = this.contentContainer;

    let ALL_ITEM_DEFS: ItemDefinition[];
    let ALL_RECIPES: Recipe[];
    let ALL_MERCHANTS: MerchantInfo[];
    let ALL_STATIONS: StationInfo[];
    try {
      [ALL_ITEM_DEFS, ALL_RECIPES, ALL_MERCHANTS, ALL_STATIONS] = await Promise.all([fetchItemDefs(), fetchRecipes(), fetchMerchants(), fetchStations()]);
    } catch (err) {
      console.error("QuestsScreen: failed to load data", err);
      resetItemDefsCache();
      recipesPromise = null;
      merchantsPromise = null;
      stationsPromise = null;
      return;
    }

    // --- Use real player data for widgets ---
    const player = window.player;
    const playerData = player?.data;
    const playerRecipeIds: number[] = playerData?.recipes || [];
    const playerInventory: Item[] = playerData?.inventory || [];
    const playerStorage: Item[] = playerData?.storage || [];

    const screenWidth = this.scene.scale.width;
    const safeLeft = getSafeAreaInsetLeft();
    const safeRight = getSafeAreaInsetRight();
    // leftColX = 80 + safeLeft clears the side buttons (squad/ranks/settings) overlaid
    // on the left edge — don't reduce it below that. (The old extra +56 on top of that
    // clearance, and mirroring the same 80px margin on the right edge where there are no
    // side buttons to clear, ate so much width that the two columns always overlapped at
    // the fixed 720px-wide desktop resolution — see RIGHT_EDGE_MARGIN below — silently
    // forcing the single-column fallback on desktop even though there was plenty of
    // visual space once the game canvas was scaled up to fill the browser window.)
    const leftColX = 80 + safeLeft;
    const CARD_OUTER_W = QUEST_WIDGET_W + 8; // card width incl. its own outer ring inset
    // Right edge has no side buttons to clear, so it only needs a small cosmetic margin —
    // not the left edge's 80px functional clearance.
    const RIGHT_EDGE_MARGIN = 24;
    const rightCardRightEdge = screenWidth - (RIGHT_EDGE_MARGIN + safeRight);
    // rightColX + 40 is the right-column cards' own x (see recipeWidgetX/dailyChallengeX
    // below); +4 accounts for their bgX(8) - outer ring(4) offset to the visual edge.
    const rightColX = rightCardRightEdge - CARD_OUTER_W - 44;

    // Filter ALL_RECIPES to only those the player has unlocked
    const recipes: Recipe[] = ALL_RECIPES.filter(r => playerRecipeIds.includes(r.itemId));

    // Build material counts from inventory and storage
    const playerMaterialCounts: Record<number, number> = {};
    for (const item of [...playerInventory, ...playerStorage]) {
      const id = item.definition.id;
      playerMaterialCounts[id] = (playerMaterialCounts[id] || 0) + 1;
    }

    /* CRAFT WIDGET */
    // Find the station closest to upgrade (fewest missing materials for next level)
    const playerStations = playerData?.stations || [];
    let bestPlayerStation: { name: string; level: number } | null = null;
    let bestStationInfo: StationInfo | null = null;
    let craftMinMissing = Infinity;
    for (const ps of playerStations) {
      const stationKey = ps.name.replace("-station", "").toUpperCase();
      const info = ALL_STATIONS.find(s => s.name === stationKey);
      if (!info) continue;
      const nextLevelEntry = info.levels.find(l => l.level === ps.level + 1);
      if (!nextLevelEntry || nextLevelEntry.materials.length === 0) continue;
      let missing = 0;
      for (const mat of nextLevelEntry.materials) {
        const owned = playerMaterialCounts[mat.itemId] || 0;
        if (owned < mat.quantity) missing += mat.quantity - owned;
      }
      if (missing < craftMinMissing) {
        craftMinMissing = missing;
        bestPlayerStation = ps;
        bestStationInfo = info;
      }
    }
    const stationIconKey = bestPlayerStation
      ? `crafting-${bestPlayerStation.name.replace("-station", "")}-icon`
      : "crafting-weapon-icon";
    const stationIcon = { spriteSheet: stationIconKey, spriteX: 0, spriteY: 0 };
    const currentLevel = bestPlayerStation?.level ?? 1;
    const craftNextLevel = bestStationInfo?.levels.find(l => l.level === currentLevel + 1);
    const craftMaterials = craftNextLevel
      ? craftNextLevel.materials.map(mat => ({
          def: ALL_ITEM_DEFS.find(def => def.id === mat.itemId) || ALL_ITEM_DEFS[0],
          required: mat.quantity,
          owned: playerMaterialCounts[mat.itemId] || 0,
        }))
      : [];
    const craftWidget = new CraftWidget(
      this.scene,
      0,
      0,
      stationIcon,
      craftMaterials,
      currentLevel,
    );

    // "Fully upgraded" means every station (not just the ones the player has already
    // interacted with) has no further level to work toward — checked against
    // ALL_STATIONS rather than playerStations so a station the player never touched
    // (implicitly level 1) still counts as having room to upgrade.
    const allStationsMaxed = !ALL_STATIONS.some(info => {
      const owned = playerStations.find(ps => ps.name.replace("-station", "").toUpperCase() === info.name);
      const stationLevel = owned?.level ?? 1;
      const nextLevelEntry = info.levels.find(l => l.level === stationLevel + 1);
      return !!nextLevelEntry && nextLevelEntry.materials.length > 0;
    });

    // Two-column layout needs room for both cards without the columns touching; narrow
    // iPhones (e.g. landscape iPhone SE, ~667px wide) don't have that room since each
    // card is a fixed QUEST_WIDGET_W, so fall back to one stacked column that reuses the
    // vertical scroll set up below instead of letting the columns collide.
    const leftCardRightEdge = leftColX + 8 + QUEST_WIDGET_W;
    const rightCardLeftEdge = rightColX + 40 + 8;
    const MIN_COLUMN_GAP = 24;
    const singleColumn = rightCardLeftEdge - leftCardRightEdge < MIN_COLUMN_GAP;

    const craftWidgetX = leftColX;
    const craftWidgetY = 56;
    const WIDGET_SLOT_H = 100; // widget bg height (~86px) + gap
    // Gap to the discord button below it — bigger than WIDGET_SLOT_H since ModernButton
    // is itself ~36px tall centered on its y, so it needs clearance beyond the widget's
    // own ~84px height to avoid crowding it.
    const DAILY_CHALLENGE_SLOT_H = 104;
    // Two-column right column pairs recipeWidget (bg top inset -20, ~86px tall — see bgY/bgH
    // in recipe-widget.ts) with dailyChallengeWidget (bg top inset -16, fixed 84px — see
    // WIDGET_H in daily-challenge-widget.ts). Those insets/heights don't match gauntletWidget
    // (-16, 84px) + craft/merchantWidget (-20, ~86px) on the left, so reusing WIDGET_SLOT_H
    // as-is for both left and right columns left the right column's gap ~6px bigger. This
    // offset is WIDGET_SLOT_H shrunk by that difference so both columns' gaps match.
    const RIGHT_COL_ROW2_OFFSET = WIDGET_SLOT_H - 6;

    // Gauntlet widget takes the top-left slot (swapped with the craft widget, which
    // moves down to the bottom slot below stars/discord — see gauntletWidgetY below);
    // all other widgets' positions are computed relative to this slot's coordinates,
    // not the widget object placed in it, so nothing else needs to change.
    const gauntletWidget = new GauntletWidget(this.scene, craftWidgetX, craftWidgetY, () => this.onOpenGauntlet());
    content.add(gauntletWidget);

    /* MERCHANT WIDGET */
    // Find the merchant closest to upgrade (fewest missing fetchItems for next level)
    const playerMerchants = playerData?.merchants || [];
    let bestMerchantInfo: MerchantInfo | null = null;
    let bestMerchantLevel = 1;
    let minMerchantMissing = Infinity;
    for (const pm of playerMerchants) {
      const info = ALL_MERCHANTS.find(m => m.id === pm.id);
      if (!info) continue;
      const nextLevelData = info.levels[pm.level]; // levels[level] = requirements to reach level+1
      if (!nextLevelData || nextLevelData.fetchItems.length === 0) continue;
      let missing = 0;
      for (const fi of nextLevelData.fetchItems) {
        const owned = playerMaterialCounts[fi.id] || 0;
        const required = fi.count || 1;
        if (owned < required) missing += required - owned;
      }
      if (missing < minMerchantMissing) {
        minMerchantMissing = missing;
        bestMerchantInfo = info;
        bestMerchantLevel = pm.level;
      }
    }
    const merchantIcon = bestMerchantInfo
      ? { spriteSheet: bestMerchantInfo.spritesheet.replace(".png", ""), spriteX: bestMerchantInfo.spriteX, spriteY: bestMerchantInfo.spriteY }
      : { spriteSheet: "enemies/merchants", spriteX: 0, spriteY: 0 };
    const merchantMaterials = bestMerchantInfo
      ? bestMerchantInfo.levels[bestMerchantLevel].fetchItems.map(fi => ({
          def: ALL_ITEM_DEFS.find(def => def.id === fi.id) || ALL_ITEM_DEFS[0],
          required: fi.count || 1,
          owned: playerMaterialCounts[fi.id] || 0,
        }))
      : [];
    const merchantLevel = bestMerchantLevel;
    const merchantWidget = new MerchantWidget(
      this.scene,
      0,
      0,
      merchantIcon,
      merchantMaterials,
      merchantLevel,
    );

    /* RECIPE WIDGET */
    const recipeWidget = new RecipeWidget(
      this.scene,
      0,
      0,
      recipes,
      ALL_ITEM_DEFS,
      playerMaterialCounts
    );

    // In the two-column layout: left column is craft/merchant/stars, right column is
    // recipe/daily-challenge/discord. When there isn't room for two columns, stack all
    // six in one column instead, in the same reading order (row by row, left then right).
    let merchantWidgetX: number, merchantWidgetY: number;
    let recipeWidgetX: number, recipeWidgetY: number;
    let dailyChallengeX: number, dailyChallengeY: number;
    let starsY: number;
    let discordBaseX: number, discordY: number;

    if (singleColumn) {
      recipeWidgetX = leftColX;
      recipeWidgetY = craftWidgetY + WIDGET_SLOT_H;
      merchantWidgetX = leftColX;
      merchantWidgetY = recipeWidgetY + WIDGET_SLOT_H;
      dailyChallengeX = leftColX;
      dailyChallengeY = merchantWidgetY + WIDGET_SLOT_H;
      starsY = dailyChallengeY + DAILY_CHALLENGE_SLOT_H + 24;
      discordBaseX = leftColX;
      discordY = starsY + 90;
    } else {
      recipeWidgetX = rightColX + 40;
      recipeWidgetY = craftWidgetY;
      merchantWidgetX = leftColX;
      merchantWidgetY = craftWidgetY + WIDGET_SLOT_H;
      dailyChallengeX = recipeWidgetX; // same X as recipeWidget so their left edges line up
      dailyChallengeY = craftWidgetY + RIGHT_COL_ROW2_OFFSET;
      starsY = merchantWidgetY + WIDGET_SLOT_H + 24;
      discordBaseX = dailyChallengeX;
      discordY = dailyChallengeY + DAILY_CHALLENGE_SLOT_H;
    }

    // The merchant widget's slot shows the craft widget by default — only falling
    // back to the merchant widget once every crafting station is fully upgraded and
    // there's nothing left to craft toward.
    if (allStationsMaxed) {
      merchantWidget.setPosition(merchantWidgetX, merchantWidgetY);
      content.add(merchantWidget);
    } else {
      craftWidget.setPosition(merchantWidgetX, merchantWidgetY);
      content.add(craftWidget);
    }

    recipeWidget.setPosition(recipeWidgetX, recipeWidgetY);
    content.add(recipeWidget);

    /* DAILY CHALLENGE WIDGET */
    let dailyChallengeWidget: DailyChallengeWidget | null = null;
    if (this.lastDailyChallengeData) {
      dailyChallengeWidget = new DailyChallengeWidget(this.scene, dailyChallengeX, dailyChallengeY, this.lastDailyChallengeData);
      content.add(dailyChallengeWidget);
    }
    this.fetchDailyChallenge(token, content, dailyChallengeX, dailyChallengeY, celebrateOnRender, dailyChallengeWidget);

    /* DAILY REWARD WIDGET */
    const dailyExtractions = playerData?.dailyExtractions ?? 0;

    // DailyStarsComponent doesn't draw its own card, so give it a black rounded panel
    // matching the other cards' width/alignment, and center the component in it.
    const starsBgX = leftColX + 4;
    const starsBgW = QUEST_WIDGET_W + 8;
    const starsBg = this.scene.add.graphics();
    drawBlackPanelBackground(starsBg, starsBgX, starsY - 45, starsBgW, 70);
    content.add(starsBg);
    const leftColCenterX = starsBgX + starsBgW / 2;

    const dailyStars = new DailyStarsComponent({
      scene: this.scene,
      x: leftColCenterX,
      y: starsY,
      dailyExtractions,
      starSize: 40, // 2x the component's default 20px
      label: "Daily Extractions",
      labelFontSize: "18px",
      animateAllStars: false,
    });
    content.add(dailyStars.getContainer());

    /* JOIN DISCORD — centered under the widget above it */
    const dailyChallengeBgX = discordBaseX + 4; // matches DailyChallengeWidget's own bgX(8) - outer ring(4)
    const dailyChallengeBgW = QUEST_WIDGET_W + 8;
    const discordX = dailyChallengeBgX + dailyChallengeBgW / 2;
    const discordButton = new ModernButton({
      scene: this.scene,
      x: discordX,
      y: discordY,
      text: "JOIN DISCORD",
      style: "discord",
      onClick: () => this.openDiscordInvite(QuestsScreen.DISCORD_INVITE_URL),
    });
    content.add(discordButton);
  }

  // Fetched separately (not part of the Promise.all above) since it round-trips through
  // the server — building the rest of the screen shouldn't block or fail on it.
  private fetchDailyChallenge(
    token: number,
    content: Phaser.GameObjects.Container,
    x: number,
    y: number,
    celebrate: boolean,
    cachedWidget: DailyChallengeWidget | null,
  ): void {
    const onData = (data: DailyChallengeClientData | null) => {
      this.dailyChallengeListener = null;
      if (this.dailyChallengeTimeout) {
        clearTimeout(this.dailyChallengeTimeout);
        this.dailyChallengeTimeout = null;
      }
      // Stale if the screen was hidden/shown again while this was in flight.
      if (token !== this.showToken || !data) return;
      this.lastDailyChallengeData = data;
      // Swapped in place at the same position, so there's no visible gap.
      cachedWidget?.destroy();
      const widget = new DailyChallengeWidget(this.scene, x, y, data);
      content.add(widget);

      if (celebrate) {
        showConfetti(this.scene);
        this.scene.tweens.add({
          targets: widget,
          scale: { from: 1, to: 1.15 },
          duration: 220,
          yoyo: true,
          ease: "Sine.easeOut",
        });
      }
    };
    this.dailyChallengeListener = onData;
    window.socket?.once("daily-challenge-data", onData as any);
    window.socket?.emit("get-daily-challenge");

    // No dedicated ack for this event, so a stuck/outdated server (or a dropped
    // socket) would otherwise fail silently with the widget just never appearing.
    this.dailyChallengeTimeout = setTimeout(() => {
      console.warn(
        "[QuestsScreen] daily-challenge-data never arrived — is the server up to date " +
        "(get-daily-challenge handler + daily_challenge_streak table) and the socket connected?",
      );
    }, 4000);
  }

  private stopListeningForDailyChallenge(): void {
    if (this.dailyChallengeListener) {
      window.socket?.off("daily-challenge-data", this.dailyChallengeListener as any);
      this.dailyChallengeListener = null;
    }
    if (this.dailyChallengeTimeout) {
      clearTimeout(this.dailyChallengeTimeout);
      this.dailyChallengeTimeout = null;
    }
  }

  private openDiscordInvite(url: string): void {
    const inviteCode = url.split("/invite/")[1] || "WdkYnRj9Hk";
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    if (isIOS) {
      let appOpened = false;
      const onVisibilityChange = () => {
        if (document.hidden) {
          appOpened = true;
        }
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };

      document.addEventListener("visibilitychange", onVisibilityChange);
      window.open(`discord://discord.gg/${inviteCode}`, "_blank");
      setTimeout(() => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        if (!appOpened) {
          window.open(url, "_blank");
        }
      }, 1000);
      return;
    }

    if (isAndroid) {
      window.open(
        `intent://discord.gg/${inviteCode}#Intent;scheme=https;package=com.discord;end;S.browser_fallback_url=${encodeURIComponent(url)}`,
        "_blank",
      );
      return;
    }

    window.open(url, "_blank");
  }

  hide(): void {
    this.stopListeningForDailyChallenge();
  }
}
